import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { vendorsApi, unwrap } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useDayLock } from "../hooks/useDayLock";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Button, Card, Empty, Field, Icon, Input, Loader } from "../components/ui";
import { KhataRunning, KhataSummary } from "../components/KhataSummary";
import { PaymentFields, ProofLine } from "../components/PaymentFields";
import { COPY } from "../utils/copy";
import { emptyKhata, emptyProof, isCreditPayment, withProof } from "../utils/finance";
import { formatDate, formatMoney } from "../utils/format";

export default function VendorKhata() {
  const { id } = useParams();
  const { isBuilder, query } = useBuilderQuery();
  const toast = useToast();
  const { isTodayClosed, reload: reloadDays } = useDayLock();
  const { busy, run } = useAsyncAction();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [pay, setPay] = useState(emptyProof());

  const load = async () => {
    setLoading(true);
    try {
      setData(unwrap(await vendorsApi.khata(id, query)));
    } catch (error) {
      toast.error(error.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, query.builderId]);

  const settle = async (event) => {
    event.preventDefault();
    const body = withProof({ amount: Number(amount) }, pay);
    if (note.trim()) body.note = note.trim();
    const result = await run(() => vendorsApi.settle(id, body), "Udhar paid. Daily book updated.");
    if (result) {
      setAmount("");
      setNote("");
      setPay(emptyProof());
      reloadDays();
      load();
    }
  };

  if (loading) return <Loader />;
  if (!data) return <Empty title="Vendor not found" text="Go back to vendors." />;

  const vendor = data.vendor || {};
  const khata = data.khata || emptyKhata();
  const canSettle = isBuilder && khata.payable > 0 && !isTodayClosed;

  return (
    <div className="stack">
      <Link to="/vendors" className="back-link">
        <Icon name="back" size={18} /> Back to vendors
      </Link>

      <header className="page-head">
        <div>
          <p className="eyebrow">Vendor khata</p>
          <h1>{vendor.name}</h1>
          <p className="muted">{vendor.phone || "No phone"}</p>
        </div>
        <div className="head-actions">
          {khata.payable > 0 ? <Link to={`/vendors/${id}/udhar`}>Udhar expenses</Link> : null}
          <Link to={`/vendors/${id}/statement`}>Statement</Link>
        </div>
      </header>

      <KhataSummary khata={khata} />

      {canSettle ? (
        <Card>
          <h2>Pay udhar</h2>
          <p className="muted">{COPY.settleHint}</p>
          <form className="form-grid collect-form" onSubmit={settle}>
            <Field label="Amount" hint={`Maximum ${formatMoney(khata.payable)}`}>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                max={khata.payable}
                required
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </Field>
            <PaymentFields form={pay} onChange={setPay} mode="settle" label="Pay with" />
            <Field label="Note" wide>
              <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional" />
            </Field>
            <div className="hisaab">
              <Button type="submit" disabled={busy}>
                Save payment
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {isBuilder && isTodayClosed && khata.payable > 0 ? (
        <div className="banner">Today's book is closed, so udhar cannot be paid today.</div>
      ) : null}

      <Card>
        <h2>Khata history</h2>
        {(data.entries || []).length === 0 ? (
          <Empty title="No entries yet" text="Expenses and udhar payments will show here." />
        ) : (
          <div className="list">
            {data.entries.map((entry) => (
              <div key={entry.id} className="list-row">
                <div>
                  <b>{entry.title}</b>
                  <span>
                    {formatDate(entry.date)}
                    {entry.note ? ` · ${entry.note}` : ""}
                  </span>
                  <ProofLine
                    paymentType={entry.paymentType}
                    referenceNumber={entry.referenceNumber}
                    image={entry.image}
                    bank={entry.bank}
                  />
                  <KhataRunning running={entry.running} />
                </div>
                <strong className={entry.kind === "settlement" ? "plus" : isCreditPayment(entry.paymentType) ? "" : "minus"}>
                  {entry.kind === "settlement" ? "Paid " : ""}
                  {formatMoney(entry.amount)}
                </strong>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
